import React, { useState, useEffect } from 'react';
import { useAuth } from '../../App';

// MCP endpoint lives on the same server as this app, under /api/mcp
// (nginx strips /api/ and forwards to Flask, which serves the /mcp route).
const MCP_ENDPOINT = `${window.location.origin}/api/mcp`;

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }
  return (
    <button
      onClick={handleCopy}
      title="Copy to clipboard"
      style={{
        background: copied ? '#166534' : '#334155',
        border: 'none', borderRadius: 6,
        color: copied ? '#4ade80' : '#94a3b8',
        cursor: 'pointer', fontSize: 12,
        padding: '4px 12px', transition: 'all .15s',
        flexShrink: 0,
      }}
    >
      {copied ? '✓ Copied' : label}
    </button>
  );
}

function InlineRow({ label, value, mono = true }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <span style={{ fontSize: 12, color: '#64748b', minWidth: 80 }}>{label}</span>
      <span style={{
        flex: 1, background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
        padding: '6px 10px', fontSize: 12, overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        fontFamily: mono ? "'Menlo','Consolas',monospace" : 'inherit',
        color: mono ? '#a5f3fc' : '#e2e8f0',
      }}>{value}</span>
      <CopyButton text={value} />
    </div>
  );
}

function CodeBlock({ code }) {
  return (
    <div style={{ position: 'relative', marginBottom: 16 }}>
      <pre style={{
        background: '#0f172a', border: '1px solid #334155', borderRadius: 10,
        padding: '14px 16px', paddingRight: 80, overflowX: 'auto', margin: 0,
      }}>
        <code style={{ fontFamily: "'Menlo','Consolas',monospace", fontSize: 13, color: '#86efac' }}>
          {code}
        </code>
      </pre>
      <div style={{ position: 'absolute', top: 10, right: 10 }}>
        <CopyButton text={code} />
      </div>
    </div>
  );
}

const AGENTS = [
  { id: 'cursor',    label: '⚡ Cursor' },
  { id: 'claude',    label: '🤖 Claude Desktop' },
  { id: 'windsurf',  label: '🌊 Windsurf' },
  { id: 'claudeweb', label: '🌐 Claude.ai' },
  { id: 'generic',   label: '🔧 Generic' },
];

export default function McpConfigPage() {
  const { user } = useAuth();
  const [token, setToken]       = useState('');
  const [activeTab, setActiveTab] = useState('cursor');

  useEffect(() => {
    const stored = localStorage.getItem('pf_token') || '';
    setToken(stored);
  }, []);

  const displayToken   = token || 'YOUR_TOKEN';
  const bearerValue    = `Bearer ${displayToken}`;

  function configFor(agent) {
    switch (agent) {
      case 'cursor':
        return JSON.stringify({
          mcpServers: {
            'blast-radius': {
              type: 'http',
              url: MCP_ENDPOINT,
              headers: { Authorization: bearerValue },
            },
          },
        }, null, 2);
      case 'claude':
        return JSON.stringify({
          mcpServers: {
            'blast-radius': {
              command: 'npx',
              args: [
                'mcp-remote',
                MCP_ENDPOINT,
                '--transport',
                'http-only',
                '--header',
                `Authorization: ${bearerValue}`,
              ],
            },
          },
        }, null, 2);
      case 'windsurf':
        return JSON.stringify({
          mcpServers: {
            'blast-radius': {
              serverUrl: MCP_ENDPOINT,
              headers: { Authorization: bearerValue },
            },
          },
        }, null, 2);
      default:
        return `POST ${MCP_ENDPOINT}\nAuthorization: ${bearerValue}\nContent-Type: application/json`;
    }
  }

  const configPath = {
    cursor:    '~/.cursor/mcp.json',
    claude:    '~/Library/Application Support/Claude/claude_desktop_config.json',
    windsurf:  '~/.codeium/windsurf/mcp_config.json',
  };

  return (
    <div style={{ padding: '32px 24px', maxWidth: 820, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 6 }}>
          🎯 MCP Server — Blast Radius
        </h1>
        <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6 }}>
          Connect any AI agent to the blast-radius API so it can identify
          which product features are impacted by a set of pull requests.
        </p>
      </div>

      {/* Token card */}
      <div style={{
        background: token ? '#052e16' : '#1c1917',
        border: `1px solid ${token ? '#166534' : '#78350f'}`,
        borderRadius: 12, padding: '20px 24px', marginBottom: 28,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <span style={{ fontSize: 18 }}>{token ? '✅' : '⚠️'}</span>
          <span style={{ fontWeight: 600, fontSize: 15 }}>
            {token ? 'You are signed in — your token is ready' : 'Sign in to get your token'}
          </span>
        </div>

        {user && (
          <p style={{ fontSize: 13, color: '#86efac', marginBottom: 14 }}>
            Signed in as <strong>{user.name || user.email}</strong>
            {user.email ? ` (${user.email})` : ''}
          </p>
        )}

        {token ? (
          <>
            <InlineRow label="Token" value={token} />
            <InlineRow label="Auth header" value={bearerValue} />
            <p style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              This is your existing app token — no extra login needed.
              It expires after 24 hours; just sign in again to refresh it.
            </p>
          </>
        ) : (
          <p style={{ fontSize: 14, color: '#fcd34d' }}>
            You need to be signed in. Sign out and sign back in with your{' '}
            <strong>@exotel.com</strong> Google account to get a token.
          </p>
        )}
      </div>

      {/* MCP URL */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>MCP Server URL</h2>
        <InlineRow label="Endpoint" value={MCP_ENDPOINT} />
      </div>

      {/* Agent configs */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Configure your agent</h2>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
          {AGENTS.map(a => (
            <button
              key={a.id}
              onClick={() => setActiveTab(a.id)}
              style={{
                padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                cursor: 'pointer', border: '1px solid',
                background: activeTab === a.id ? '#3b82f6' : 'transparent',
                borderColor: activeTab === a.id ? '#3b82f6' : '#334155',
                color: activeTab === a.id ? '#fff' : '#94a3b8',
                transition: 'all .15s',
              }}
            >{a.label}</button>
          ))}
        </div>

        {/* Tab content */}
        <div style={{
          background: '#1e293b', border: '1px solid #334155',
          borderRadius: 10, padding: 20,
        }}>
          {activeTab === 'claudeweb' ? (
            <>
              <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>
                In a Claude Project go to <strong style={{ color: '#e2e8f0' }}>Project settings → Integrations → Add integration</strong>:
              </p>
              <ol style={{ paddingLeft: 20, fontSize: 14, color: '#94a3b8', lineHeight: 2.2 }}>
                <li>Type: <strong style={{ color: '#e2e8f0' }}>Remote MCP server</strong></li>
                <li>URL: <code style={{ fontFamily: 'monospace', background: '#0f172a', padding: '1px 5px', borderRadius: 3, color: '#a5f3fc' }}>{MCP_ENDPOINT}</code></li>
                <li>Authentication: <strong style={{ color: '#e2e8f0' }}>Custom header</strong></li>
                <li>Header name: <code style={{ fontFamily: 'monospace', background: '#0f172a', padding: '1px 5px', borderRadius: 3, color: '#a5f3fc' }}>Authorization</code></li>
                <li>Header value: <code style={{ fontFamily: 'monospace', background: '#0f172a', padding: '1px 5px', borderRadius: 3, color: '#a5f3fc' }}>{bearerValue}</code></li>
              </ol>
            </>
          ) : (
            <>
              {configPath[activeTab] && (
                <p style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
                  Edit <code style={{ fontFamily: 'monospace', background: '#0f172a', padding: '1px 5px', borderRadius: 3, color: '#a5f3fc' }}>{configPath[activeTab]}</code>
                  {activeTab === 'cursor' && ' or use Settings → Cursor Settings → MCP.'}
                  {activeTab !== 'cursor' && '. Restart after saving.'}
                </p>
              )}
              <CodeBlock code={configFor(activeTab)} />
            </>
          )}
        </div>
      </div>

      {/* Tool reference */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Available tools</h2>

        {/* analyze_blast_radius */}
        <div style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '18px 20px',
          marginBottom: 12,
        }}>
          <div style={{ marginBottom: 10 }}>
            <code style={{
              fontFamily: 'monospace', background: '#0f172a', padding: '4px 10px',
              borderRadius: 6, color: '#a5f3fc', fontSize: 14,
            }}>analyze_blast_radius</code>
          </div>
          <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>
            Given one or more PR / MR URLs, fetches changed files from the SCM provider
            and cross-references them against the feature registry to identify impacted features.
          </p>
          <CodeBlock code={JSON.stringify({
            pr_urls: [
              'https://github.com/owner/repo/pull/123',
              'https://bitbucket.org/workspace/repo/pull-requests/456',
            ],
          }, null, 2)} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['GitHub PRs', 'Bitbucket PRs', 'GitLab MRs (incl. self-hosted)', 'Gitea PRs', 'Up to 20 PRs'].map(t => (
              <span key={t} style={{
                background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                padding: '2px 10px', fontSize: 12, color: '#64748b',
              }}>{t}</span>
            ))}
          </div>
        </div>

        {/* get_features_for_file */}
        <div style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '18px 20px',
          marginBottom: 12,
        }}>
          <div style={{ marginBottom: 10 }}>
            <code style={{
              fontFamily: 'monospace', background: '#0f172a', padding: '4px 10px',
              borderRadius: 6, color: '#a5f3fc', fontSize: 14,
            }}>get_features_for_file</code>
          </div>
          <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>
            Reverse lookup — given a single code-file path, returns the product features
            whose glob patterns match it. Optionally scope to one repository.
          </p>
          <CodeBlock code={JSON.stringify({
            file_path: 'src/voice/ivr_handler.py',
            repo: 'voice',
          }, null, 2)} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['Inverse of blast radius', 'Repo optional', 'Coverage check'].map(t => (
              <span key={t} style={{
                background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                padding: '2px 10px', fontSize: 12, color: '#64748b',
              }}>{t}</span>
            ))}
          </div>
        </div>

        {/* set_features_for_file */}
        <div style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '18px 20px',
          marginBottom: 12,
        }}>
          <div style={{ marginBottom: 10 }}>
            <code style={{
              fontFamily: 'monospace', background: '#0f172a', padding: '4px 10px',
              borderRadius: 6, color: '#a5f3fc', fontSize: 14,
            }}>set_features_for_file</code>
          </div>
          <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>
            Add or update the features mapped to a code file (existing or brand-new).
            Idempotent add by default; pass{' '}
            <code style={{ fontFamily: 'monospace', background: '#0f172a', padding: '1px 5px', borderRadius: 3, color: '#a5f3fc', fontSize: 12 }}>replace: true</code>
            {' '}to make the file’s feature set exactly the given list. Same capability
            as the <strong>File Mapping</strong> page.
          </p>
          <CodeBlock code={JSON.stringify({
            file_path: 'src/voice/ivr_handler.py',
            repo_name: 'voice',
            feature_ids: ['VOICE-FEAT-007', 'VOICE-FEAT-012'],
            replace: false,
          }, null, 2)} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['Add or replace', 'Works for new files', 'Idempotent', 'By feature_id'].map(t => (
              <span key={t} style={{
                background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                padding: '2px 10px', fontSize: 12, color: '#64748b',
              }}>{t}</span>
            ))}
          </div>
        </div>

        {/* export_blast_radius_to_sheets */}
        <div style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '18px 20px',
        }}>
          <div style={{ marginBottom: 10 }}>
            <code style={{
              fontFamily: 'monospace', background: '#0f172a', padding: '4px 10px',
              borderRadius: 6, color: '#a5f3fc', fontSize: 14,
            }}>export_blast_radius_to_sheets</code>
          </div>
          <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>
            Runs the blast-radius analysis and exports the full impacted-feature list to a
            new Google Spreadsheet. Returns a shareable link — anyone with the URL can view it.
            Requires a Google OAuth access token with{' '}
            <code style={{ fontFamily: 'monospace', background: '#0f172a', padding: '1px 5px', borderRadius: 3, color: '#a5f3fc', fontSize: 12 }}>spreadsheets</code>
            {' '}and{' '}
            <code style={{ fontFamily: 'monospace', background: '#0f172a', padding: '1px 5px', borderRadius: 3, color: '#a5f3fc', fontSize: 12 }}>drive.file</code>
            {' '}scopes.
          </p>
          <CodeBlock code={JSON.stringify({
            pr_urls: ['https://github.com/owner/repo/pull/123'],
            access_token: '<google-oauth-access-token>',
          }, null, 2)} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['Creates new Spreadsheet', '2 tabs: Features + Summary', 'Public shareable link', 'Up to 20 PRs'].map(t => (
              <span key={t} style={{
                background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
                padding: '2px 10px', fontSize: 12, color: '#64748b',
              }}>{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Troubleshooting */}
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Troubleshooting</h2>
        <div style={{
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10, padding: '18px 20px',
        }}>
          {[
            ['401 Unauthorized', 'Token expired. Sign out and sign back in to the app to get a fresh token.'],
            ['No features matched', 'The changed files are in a repository not yet mapped to features. Check Repositories → Providers.'],
            ['SCM API error', 'The GitHub / Bitbucket / GitLab / Gitea access token may be expired. Update it in Repositories → Providers.'],
          ].map(([err, fix]) => (
            <div key={err} style={{ marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <code style={{
                fontFamily: 'monospace', background: '#0f172a', padding: '2px 8px',
                borderRadius: 4, color: '#fca5a5', fontSize: 12, flexShrink: 0,
              }}>{err}</code>
              <span style={{ fontSize: 13, color: '#94a3b8' }}>{fix}</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
